import type { Attribute } from "@tiptap/core"

/**
 * Returns part of an attribute description object that renders and parses
 * the attribute as an HTML data attribute with the given name.
 *
 * Based on third example under https://tiptap.dev/docs/editor/extensions/custom-extensions/extend-existing#attributes.
 */

export function renderStringAsData(tiptapName: string, htmlName = tiptapName): Partial<Attribute> {
  const dataAttr = `data-${htmlName}`

  return {
    renderHTML: (attributes) => {
      return {
        [dataAttr]: attributes[tiptapName],
      }
    },

    parseHTML: (element) => element.getAttribute(dataAttr),
  }
}

/**
 * Returns part of an attribute description object that renders and parses
 * the attribute as an HTML data attribute with the given name,
 * specialized to boolean values (data attribute is present iff true).
 */
export function renderBooleanAsData(tiptapName: string, htmlName = tiptapName): Partial<Attribute> {
  const dataAttr = `data-${htmlName}`

  return {
    renderHTML(attributes) {
      if (attributes[tiptapName]) return { [dataAttr]: "" }
      else return {}
    },
    parseHTML(element) {
      return element.hasAttribute(dataAttr)
    },
  }
}

export function renderJsonAsData(tiptapName: string, htmlName = tiptapName): Partial<Attribute> {
  const dataAttr = `data-${htmlName}`

  return {
    renderHTML: (attributes) => {
      return {
        [dataAttr]: JSON.stringify(attributes[tiptapName]),
      }
    },

    parseHTML: (element) =>
      element.hasAttribute(dataAttr) ? JSON.parse(element.getAttribute(dataAttr)!) : null,
  }
}
